import React, { useState, useEffect } from 'react';
import './App.css';
import logo from './assets/LogoImageIcon.jpeg';
import { db } from './firebase';
import { collection, addDoc, serverTimestamp, getDocs } from 'firebase/firestore';

function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    date: '',
    time: '',
    guests: '',
    specialRequests: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      // Add reservation to Firestore
      await addDoc(collection(db, 'reservations'), {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        date: formData.date,
        time: formData.time,
        guests: formData.guests,
        specialRequests: formData.specialRequests,
        createdAt: serverTimestamp(),
        status: 'pending'
      });

      setSubmitMessage('Reservation submitted successfully! We will contact you soon.');
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        date: '',
        time: '',
        guests: '',
        specialRequests: ''
      });
    } catch (error) {
      console.error('Error adding reservation:', error);
      setSubmitMessage('Error submitting reservation. Please try again.');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitMessage(''), 5000);
    }
  };

  const sliderImages = [
    "https://images.unsplash.com/photo-1544025162-d76694265947?w=1200&q=80",
    "https://images.unsplash.com/photo-1555939594-58d7cb561ad1?w=1200&q=80",
    "https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=1200&q=80",
    "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=1200&q=80"
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % sliderImages.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [sliderImages.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % sliderImages.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + sliderImages.length) % sliderImages.length);
  };

  const [menuItems, setMenuItems] = useState([]);

  useEffect(() => {
    const fetchMenuItems = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'foodCatalog'));
        const items = querySnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            name: data.name || "Unknown Dish",
            description: data.description || "Delicious non-veg dish",
            price: `₹${data.price || 0}`,
            image: data.imageUrl || "https://images.unsplash.com/photo-1544025162-d76694265947?w=500&q=80"
          };
        });
        setMenuItems(items);
      } catch (error) {
        console.error("Error fetching menu items:", error);
      }
    };
    
    fetchMenuItems();
  }, []);

  const testimonials = [
    {
      id: 1,
      name: "Rajesh Kumar",
      rating: 5,
      comment: "Best restaurant in the city! The biryani is absolutely amazing.",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&q=80"
    },
    {
      id: 2,
      name: "Priya Sharma",
      rating: 5,
      comment: "Authentic taste and great ambiance. Highly recommended!",
      image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&q=80"
    },
    {
      id: 3,
      name: "Amit Patel",
      rating: 5,
      comment: "The food quality is outstanding. Will definitely visit again!",
      image: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&q=80"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed w-full bg-white/95 backdrop-blur-sm shadow-md z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center">
              <img src={logo} alt="KGF Khanana" className="h-16 w-auto" />
            </div>
            
            {/* Desktop Menu */}
            <div className="hidden md:flex space-x-8">
              <a href="#home" className="text-gray-700 hover:text-gold-600 font-medium transition">Home</a>
              <a href="#about" className="text-gray-700 hover:text-gold-600 font-medium transition">About</a>
              <a href="#menu" className="text-gray-700 hover:text-gold-600 font-medium transition">Menu</a>
              <a href="#gallery" className="text-gray-700 hover:text-gold-600 font-medium transition">Gallery</a>
              <a href="#contact" className="text-gray-700 hover:text-gold-600 font-medium transition">Contact</a>
            </div>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 rounded-md text-gray-700 hover:text-gold-600"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"} />
              </svg>
            </button>
          </div>

          {/* Mobile Menu */}
          {isMenuOpen && (
            <div className="md:hidden pb-4">
              <a href="#home" className="block py-2 text-gray-700 hover:text-gold-600">Home</a>
              <a href="#about" className="block py-2 text-gray-700 hover:text-gold-600">About</a>
              <a href="#menu" className="block py-2 text-gray-700 hover:text-gold-600">Menu</a>
              <a href="#gallery" className="block py-2 text-gray-700 hover:text-gold-600">Gallery</a>
              <a href="#contact" className="block py-2 text-gray-700 hover:text-gold-600">Contact</a>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative h-screen flex items-center justify-center overflow-hidden pt-20">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=1600&q=80" 
            alt="Restaurant Interior"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/50"></div>
        </div>
        
        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
          <h2 className="text-5xl md:text-7xl font-serif font-bold text-white mb-6 text-shadow">
            KGF Khanana - Best Restaurant in Tumkur
          </h2>
          <p className="text-xl md:text-2xl text-white mb-8 text-shadow">
            Famous Family Restaurant in Tumakuru | Best Non-Veg Hotel | Order Online through KGF Khanana App
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="#menu" className="bg-gold-600 text-white px-8 py-4 rounded-full font-semibold hover:bg-gold-500 transition transform hover:scale-105">
              View Menu
            </a>
            <a href="#contact" className="bg-white text-gray-900 px-8 py-4 rounded-full font-semibold hover:bg-gray-100 transition transform hover:scale-105">
              Reserve Table
            </a>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-serif font-bold text-gray-900 mb-6">
                Best Family Restaurant in Tumkur & Tumakuru
              </h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                KGF Khanana is the best restaurant in Tumkur and the most famous non-veg hotel in Tumakuru. 
                As Tumkur's top-rated family restaurant, we specialize in authentic non-vegetarian cuisine featuring 
                the finest chicken, mutton, fish, and seafood delicacies. Download the KGF Khanana app for easy 
                online food ordering and delivery in Tumkur.
              </p>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Recognized as the best non-veg restaurant in Tumakuru, our expert chefs bring decades of experience 
                in crafting traditional recipes. From our famous biryani to succulent kebabs, every dish at this 
                popular restaurant in Tumkur is prepared with premium ingredients. Visit the best family hotel in 
                Tumkur for an affordable and unforgettable dining experience.
              </p>
              <div className="grid grid-cols-3 gap-6 mt-8">
                <div className="text-center">
                  <div className="text-3xl font-bold text-gold-600">10+</div>
                  <div className="text-gray-600 mt-1">Years</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-gold-600">50+</div>
                  <div className="text-gray-600 mt-1">Dishes</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-gold-600">10k+</div>
                  <div className="text-gray-600 mt-1">Customers</div>
                </div>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=800&q=80" 
                alt="Restaurant Ambiance"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -right-6 w-64 h-64 bg-gold-600 rounded-2xl -z-10"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Menu Section */}
      <section id="menu" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-gray-900 mb-4">
              Famous Non-Veg Dishes in Tumkur
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Best Biryani, Chicken & Mutton Restaurant in Tumakuru - Order through KGF Khanana App
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {menuItems.map((item) => (
              <div key={item.id} className="group bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition transform hover:-translate-y-2">
                <div className="relative h-64 overflow-hidden">
                  <img 
                    src={item.image} 
                    alt={item.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition duration-500"
                  />
                </div>
                <div className="p-6">
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="text-2xl font-serif font-bold text-gray-900">{item.name}</h3>
                    <span className="text-2xl font-bold text-gold-600">{item.price}</span>
                  </div>
                  <p className="text-gray-600 leading-relaxed">{item.description}</p>
                  <a 
                    href="https://play.google.com/store/apps/details?id=com.kgfkhana&pcampaignid=web_share"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-4 w-full bg-gold-600 text-white py-3 rounded-full font-semibold hover:bg-gold-500 transition block text-center"
                  >
                    Order Now
                  </a>
                </div>
              </div>
            ))}
          </div>

          {/* Download App CTA */}
          <div className="mt-16 bg-gradient-to-r from-gold-600 to-gold-500 rounded-3xl p-12 text-center text-white">
            <h3 className="text-3xl md:text-4xl font-serif font-bold mb-4">
              Download KGF Khanana App - Best Food Delivery App in Tumkur
            </h3>
            <p className="text-lg mb-8 max-w-2xl mx-auto">
              Order food online in Tumakuru with KGF Khanana restaurant app! Get exclusive deals, faster checkout, 
              and home delivery from Tumkur's best non-veg hotel. Top rated food ordering app in Tumkur.
            </p>
            <a 
              href="https://play.google.com/store/apps/details?id=com.kgfkhana&pcampaignid=web_share"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center bg-black text-white px-8 py-4 rounded-full font-semibold hover:bg-gray-900 transition transform hover:scale-105"
            >
              <svg className="w-8 h-8 mr-3" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
              </svg>
              Get it on Google Play
            </a>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="gallery" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-gray-900 mb-4">
              Gallery
            </h2>
            <p className="text-xl text-gray-600">
              A visual journey through our culinary excellence
            </p>
          </div>

          {/* Slider Section */}
          <div className="mb-12">
            <div className="relative h-[500px] rounded-3xl overflow-hidden shadow-2xl">
              {sliderImages.map((img, index) => (
                <div
                  key={index}
                  className={`absolute inset-0 transition-opacity duration-1000 ${
                    index === currentSlide ? 'opacity-100' : 'opacity-0'
                  }`}
                >
                  <img
                    src={img}
                    alt={`Slide ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                </div>
              ))}
              
              {/* Slider Controls */}
              <button
                onClick={prevSlide}
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/20 backdrop-blur-sm hover:bg-white/40 text-white p-3 rounded-full transition"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                onClick={nextSlide}
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/20 backdrop-blur-sm hover:bg-white/40 text-white p-3 rounded-full transition"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>

              {/* Slider Indicators */}
              <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
                {sliderImages.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`h-2 rounded-full transition-all ${
                      index === currentSlide ? 'w-8 bg-gold-600' : 'w-2 bg-white/50'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Animated Grid Gallery */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
            {[
              { img: "https://images.unsplash.com/photo-1603360946369-dc9bb6258143?w=500&q=80", title: "Chicken Kebabs", delay: "0" },
              { img: "https://images.unsplash.com/photo-1603894584373-5ac82b2ae398?w=500&q=80", title: "Butter Chicken", delay: "100" },
              { img: "https://images.unsplash.com/photo-1599487488170-d11ec9c172f0?w=500&q=80", title: "Tandoori Platter", delay: "200" },
              { img: "https://images.unsplash.com/photo-1574484284002-952d92456975?w=500&q=80", title: "Mutton Curry", delay: "300" },
              { img: "https://images.unsplash.com/photo-1563379091339-03b21ab4a4f8?w=500&q=80", title: "Biryani Special", delay: "0" },
              { img: "https://images.unsplash.com/photo-1626645738196-c2a7c87a8f58?w=500&q=80", title: "Fish Tikka", delay: "100" },
              { img: "https://images.unsplash.com/photo-1633504581786-316c8002b1b9?w=500&q=80", title: "Prawns Masala", delay: "200" },
              { img: "https://images.unsplash.com/photo-1565557623262-b51c2513a641?w=500&q=80", title: "Tikka Masala", delay: "300" }
            ].map((item, index) => (
              <div
                key={index}
                className="relative overflow-hidden rounded-2xl aspect-square group shadow-lg transform hover:-translate-y-2 transition-all duration-500"
                style={{ animationDelay: `${item.delay}ms` }}
              >
                <img
                  src={item.img}
                  alt={item.title}
                  className="w-full h-full object-cover group-hover:scale-125 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500">
                  <div className="absolute bottom-0 left-0 right-0 p-6 text-white transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
                    <h4 className="text-lg font-serif font-bold">{item.title}</h4>
                  </div>
                </div>
                {/* Animated Border */}
                <div className="absolute inset-0 border-2 border-gold-600 rounded-2xl scale-0 group-hover:scale-100 transition-transform duration-500"></div>
              </div>
            ))}
          </div>

          {/* Masonry Style Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {/* Tall Image 1 */}
            <div className="row-span-2">
              <div className="relative overflow-hidden rounded-2xl h-full group shadow-xl">
                <img
                  src="https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=500&q=80"
                  alt="Restaurant Interior"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-500 flex items-center justify-center">
                  <span className="text-white font-serif text-xl font-bold opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    Ambiance
                  </span>
                </div>
              </div>
            </div>

            {/* Regular Images */}
            <div className="relative overflow-hidden rounded-2xl aspect-square group shadow-lg">
              <img
                src="https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=500&q=80"
                alt="Fine Dining"
                className="w-full h-full object-cover group-hover:scale-110 group-hover:rotate-3 transition-all duration-700"
              />
            </div>

            <div className="relative overflow-hidden rounded-2xl aspect-square group shadow-lg">
              <img
                src="https://images.unsplash.com/photo-1555939594-58d7cb561ad1?w=500&q=80"
                alt="Food Plating"
                className="w-full h-full object-cover group-hover:scale-110 group-hover:rotate-3 transition-all duration-700"
              />
            </div>

            {/* Tall Image 2 */}
            <div className="row-span-2">
              <div className="relative overflow-hidden rounded-2xl h-full group shadow-xl">
                <img
                  src="https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=500&q=80"
                  alt="Chef Cooking"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gold-600/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <div className="absolute bottom-0 p-6 text-white">
                    <h4 className="text-xl font-serif font-bold">Chef's Special</h4>
                    <p className="text-sm mt-1">Crafted with passion</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative overflow-hidden rounded-2xl aspect-square group shadow-lg">
              <img
                src="https://images.unsplash.com/photo-1567620905732-2d1ec7ab7445?w=500&q=80"
                alt="Desserts"
                className="w-full h-full object-cover group-hover:scale-110 group-hover:rotate-3 transition-all duration-700"
              />
            </div>

            <div className="relative overflow-hidden rounded-2xl aspect-square group shadow-lg">
              <img
                src="https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?w=500&q=80"
                alt="Fresh Ingredients"
                className="w-full h-full object-cover group-hover:scale-110 group-hover:rotate-3 transition-all duration-700"
              />
            </div>
          </div>

          {/* Horizontal Scroll Gallery */}
          <div className="mb-12">
            <h3 className="text-2xl font-serif font-bold text-gray-900 mb-6">Featured Dishes</h3>
            <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide snap-x snap-mandatory">
              {[
                "https://images.unsplash.com/photo-1633945274405-b6c8069047b0?w=600&q=80",
                "https://images.unsplash.com/photo-1610057099443-fde8c4d50f91?w=600&q=80",
                "https://images.unsplash.com/photo-1529042410759-befb1204b468?w=600&q=80",
                "https://images.unsplash.com/photo-1534604973900-c43ab4c2e8c0?w=600&q=80",
                "https://images.unsplash.com/photo-1606787366850-de6330128bfc?w=600&q=80",
                "https://images.unsplash.com/photo-1512621776951-a57141f2eefd?w=600&q=80"
              ].map((img, index) => (
                <div
                  key={index}
                  className="min-w-[300px] md:min-w-[400px] snap-center relative overflow-hidden rounded-2xl aspect-video group shadow-xl flex-shrink-0"
                >
                  <img
                    src={img}
                    alt={`Featured ${index + 1}`}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    <div className="h-full flex items-center p-6 text-white">
                      <div className="transform -translate-x-4 group-hover:translate-x-0 transition-transform duration-500">
                        <span className="inline-block bg-gold-600 px-3 py-1 rounded-full text-xs font-semibold mb-2">
                          Must Try
                        </span>
                        <h4 className="text-xl font-serif font-bold">Signature Dish #{index + 1}</h4>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* View More Button */}
          <div className="text-center">
            <a
              href="https://play.google.com/store/apps/details?id=com.kgfkhana&pcampaignid=web_share"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-gradient-to-r from-gold-600 to-gold-500 text-white px-10 py-4 rounded-full font-semibold hover:shadow-2xl transition-all transform hover:scale-105"
            >
              View Full Gallery in App
            </a>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-gray-900 mb-4">
              What Our Customers Say
            </h2>
            <p className="text-xl text-gray-600">
              Don't just take our word for it
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <div key={testimonial.id} className="bg-gray-50 rounded-2xl p-8 shadow-lg hover:shadow-xl transition">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <svg key={i} className="w-5 h-5 text-gold-600" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <p className="text-gray-700 mb-6 leading-relaxed italic">
                  "{testimonial.comment}"
                </p>
                <div className="flex items-center">
                  <img 
                    src={testimonial.image} 
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover mr-4"
                  />
                  <div>
                    <h4 className="font-semibold text-gray-900">{testimonial.name}</h4>
                    <p className="text-sm text-gray-600">Verified Customer</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h2 className="text-4xl md:text-5xl font-serif font-bold mb-6">
                Visit Tumkur's Best Family Restaurant
              </h2>
              <p className="text-lg text-gray-300 mb-8">
                Come experience the best non-veg hotel in Tumakuru. KGF Khanana - Top rated restaurant in Tumkur, 
                open 7 days a week for family dining and online food ordering.
              </p>

              <div className="space-y-6">
                <div className="flex items-start">
                  <svg className="w-6 h-6 text-gold-600 mt-1 mr-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Address</h3>
                    <p className="text-gray-300">KGF Khanana Restaurant<br/>Tumkur, Karnataka - 572101<br/>Best Restaurant in Tumkur</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <svg className="w-6 h-6 text-gold-600 mt-1 mr-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Phone</h3>
                    <p className="text-gray-300">+91 9972997283<br/>+91 9972087283</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <svg className="w-6 h-6 text-gold-600 mt-1 mr-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Hours</h3>
                    <p className="text-gray-300">Mon - Sun: 11:00 AM - 11:00 PM<br/>Kitchen Closes: 10:30 PM</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white text-gray-900 rounded-2xl p-8">
              <h3 className="text-2xl font-serif font-bold mb-6">Book Table at Best Restaurant in Tumakuru</h3>
              
              {submitMessage && (
                <div className={`mb-4 p-4 rounded-lg ${submitMessage.includes('Error') ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                  {submitMessage}
                </div>
              )}

              <form className="space-y-4" onSubmit={handleSubmit}>
                <div>
                  <input 
                    type="text" 
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Your Name"
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-gold-600 focus:ring-2 focus:ring-gold-600 focus:outline-none"
                  />
                </div>
                <div>
                  <input 
                    type="email" 
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Email Address"
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-gold-600 focus:ring-2 focus:ring-gold-600 focus:outline-none"
                  />
                </div>
                <div>
                  <input 
                    type="tel" 
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="Phone Number"
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-gold-600 focus:ring-2 focus:ring-gold-600 focus:outline-none"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <input 
                    type="date" 
                    name="date"
                    value={formData.date}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-gold-600 focus:ring-2 focus:ring-gold-600 focus:outline-none"
                  />
                  <input 
                    type="time" 
                    name="time"
                    value={formData.time}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-gold-600 focus:ring-2 focus:ring-gold-600 focus:outline-none"
                  />
                </div>
                <div>
                  <select 
                    name="guests"
                    value={formData.guests}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-gold-600 focus:ring-2 focus:ring-gold-600 focus:outline-none"
                  >
                    <option value="">Number of Guests</option>
                    <option value="1-2">1-2 People</option>
                    <option value="3-4">3-4 People</option>
                    <option value="5-6">5-6 People</option>
                    <option value="7+">7+ People</option>
                  </select>
                </div>
                <div>
                  <textarea 
                    name="specialRequests"
                    value={formData.specialRequests}
                    onChange={handleInputChange}
                    placeholder="Special Requests (Optional)"
                    rows="3"
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-gold-600 focus:ring-2 focus:ring-gold-600 focus:outline-none"
                  ></textarea>
                </div>
                <button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="w-full bg-gold-600 text-white py-4 rounded-full font-semibold hover:bg-gold-500 transition transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Submitting...' : 'Book Table'}
                </button>
              </form>
            </div>
          </div>

          {/* Map Section */}
          <div className="mt-16 rounded-2xl overflow-hidden shadow-2xl h-96 bg-gray-800">
            <iframe
              title="KGF Khanana Location Map"
              src="https://www.google.com/maps/embed?pb=!1m17!1m12!1m3!1d3879.9094801358524!2d77.05291617968045!3d13.47967518907086!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m2!1m1!2zMTPCsDI4JzQzLjQiTiA3N8KwMDMnMTAuNCJF!5e0!3m2!1sen!2sin!4v1785047734318!5m2!1sen!2sin"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="strict-origin-when-cross-origin"
            ></iframe>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <img src={logo} alt="KGF Khanana" className="h-12 w-auto mb-4" />
              <p className="text-gray-400">
                Serving authentic flavors with passion and excellence since 2014.
              </p>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><a href="#home" className="text-gray-400 hover:text-gold-600 transition">Home</a></li>
                <li><a href="#about" className="text-gray-400 hover:text-gold-600 transition">About</a></li>
                <li><a href="#menu" className="text-gray-400 hover:text-gold-600 transition">Menu</a></li>
                <li><a href="#contact" className="text-gray-400 hover:text-gold-600 transition">Contact</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Services</h4>
              <ul className="space-y-2">
                <li><a href="#" className="text-gray-400 hover:text-gold-600 transition">Dine In</a></li>
                <li><a href="#" className="text-gray-400 hover:text-gold-600 transition">Take Away</a></li>
                <li><a href="#" className="text-gray-400 hover:text-gold-600 transition">Home Delivery</a></li>
                <li><a href="#" className="text-gray-400 hover:text-gold-600 transition">Catering</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Get Our App</h4>
              <a 
                href="https://play.google.com/store/apps/details?id=com.kgfkhana&pcampaignid=web_share"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block mb-6"
              >
                <img 
                  src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg" 
                  alt="Get it on Google Play"
                  className="h-12 hover:opacity-80 transition"
                />
              </a>
              <h4 className="text-lg font-semibold mb-4">Follow Us</h4>
              <div className="flex space-x-4">
                <a href="#" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-gold-600 transition">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                </a>
                <a href="#" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-gold-600 transition">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                </a>
                <a href="#" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-gold-600 transition">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/></svg>
                </a>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400">
            <p>&copy; 2026 KGF Khanana. All rights reserved. Crafted with passion for food lovers.</p>
            <p className="mt-2">
              <a 
                href="https://images-2025-kallesh.s3.eu-north-1.amazonaws.com/PrivacyPolicy.html" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gold-600 hover:text-gold-500 transition underline"
              >
                Privacy Policy
              </a>
            </p>
            <p className="mt-2 text-sm">
              Designed and Developed by{' '}
              <a 
                href="https://www.linkedin.com/in/kallesh-d-r-087008235/?originalSubdomain=in" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gold-600 hover:text-gold-500 transition font-semibold"
              >
                Kallesh D R
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
